import { useState, useEffect, useCallback } from 'react';
import { api, ChatChannelResponse, ChatChannelMemberResponse } from '@/lib/api';

const MEMBER_PAGE_SIZE = 100;

interface UseChatChannelReturn {
  channel: ChatChannelResponse | null;
  members: ChatChannelMemberResponse[];
  memberCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  join: () => Promise<boolean>;
  leave: () => Promise<boolean>;
}

export function useChatChannel(channelId: string | null): UseChatChannelReturn {
  const [channel, setChannel] = useState<ChatChannelResponse | null>(null);
  const [members, setMembers] = useState<ChatChannelMemberResponse[]>([]);
  const [memberCount, setMemberCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChannel = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setError(null);
    try {
      const [channelResult, membersResult] = await Promise.all([
        api.getChatChannel(channelId),
        api.getChatChannelMembers(channelId, { limit: MEMBER_PAGE_SIZE }),
      ]);
      setChannel(channelResult.channel);
      setMembers(membersResult.members);
      setMemberCount(membersResult.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  const refresh = useCallback(() => {
    fetchChannel();
  }, [fetchChannel]);

  const join = useCallback(async (): Promise<boolean> => {
    if (!channelId) return false;
    try {
      await api.joinChatChannel(channelId);
      await fetchChannel();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join channel');
      return false;
    }
  }, [channelId, fetchChannel]);

  const leave = useCallback(async (): Promise<boolean> => {
    if (!channelId) return false;
    try {
      await api.leaveChatChannel(channelId);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave channel');
      return false;
    }
  }, [channelId]);

  useEffect(() => {
    if (channelId) {
      fetchChannel();
    } else {
      setChannel(null);
      setMembers([]);
      setMemberCount(0);
    }
  }, [channelId, fetchChannel]);

  return { channel, members, memberCount, loading, error, refresh, join, leave };
}
